import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/resume/Header";
import { HeroSection } from "@/components/resume/HeroSection";
import { SkillsSection } from "@/components/resume/SkillsSection";
import { ProjectsSection } from "@/components/resume/ProjectsSection";
import { ExperienceSection } from "@/components/resume/ExperienceSection";
import { EducationSection } from "@/components/resume/EducationSection";
import { ContactSection } from "@/components/resume/ContactSection";
import { Footer } from "@/components/resume/Footer";
import { ScrollToTop } from "@/components/ScrollToTop";
import { usePageView } from "@/hooks/usePageView";
import { SeoHead } from "@/components/SeoHead";
import {
  HeroSkeleton,
  SkillsSkeleton,
  ProjectsSkeleton,
  ExperienceSkeleton,
  EducationSkeleton,
  ContactSkeleton,
} from "@/components/ui/skeleton-section";

const Resume = () => {
  // Track page view
  usePageView({ pagePath: "/" });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile")
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: competencies, isLoading: competenciesLoading } = useQuery({
    queryKey: ["competencies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("core_competencies")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: experiences, isLoading: experiencesLoading } = useQuery({
    queryKey: ["experiences"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_experience")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: skills, isLoading: skillsLoading } = useQuery({
    queryKey: ["skills"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skills")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: education, isLoading: educationLoading } = useQuery({
    queryKey: ["education"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("education")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/10 selection:text-primary">
      <SeoHead
        pageType="resume"
        entity={{
          name: profile?.full_name,
          headline: profile?.summary?.split('.')[0],
          summary: profile?.summary,
          image_url: profile?.profile_photo_url,
        }}
      />
      <Header profile={profile} />

      <main className="space-y-0">
        <section className="animate-fade-in">
          {profileLoading ? (
            <HeroSkeleton />
          ) : (
            <HeroSection profile={profile} />
          )}
        </section>

        <section className="animate-slide-up delay-100">
          {skillsLoading || competenciesLoading ? (
            <SkillsSkeleton />
          ) : (
            <SkillsSection skills={skills || []} competencies={competencies} />
          )}
        </section>

        <section className="animate-slide-up delay-200">
          {projectsLoading ? (
            <ProjectsSkeleton />
          ) : (
            <ProjectsSection projects={projects || []} />
          )}
        </section>

        <section className="animate-slide-up delay-300">
          {experiencesLoading ? (
            <ExperienceSkeleton />
          ) : (
            <ExperienceSection experiences={experiences || []} />
          )}
        </section>

        <section className="animate-slide-up delay-400">
          {educationLoading ? (
            <EducationSkeleton />
          ) : (
            <EducationSection education={education || []} />
          )}
        </section>

        <section className="animate-slide-up delay-500">
          {profileLoading ? (
            <ContactSkeleton />
          ) : (
            <ContactSection profile={profile} />
          )}
        </section>
      </main>

      <Footer profile={profile} />
      <ScrollToTop />
    </div>
  );
};

export default Resume;
